/**
 * チャット履歴の検索を、描画済みの DOM ではなくメッセージのデータから行う。
 * 仮想スクロールで画面外のブロックは描かれないため、一致の数え上げと位置決めはここで行い、
 * DOM は描かれているブロックのハイライトだけに使う (transcript-highlight.ts)。
 *
 * 件数を画面の表示と揃えるため、Markdown は ChatMessage と同じ変換 (remark + GFM → hast) を通し、
 * 画面に描かれる文字だけを検索対象にする。
 */

import type { Element, Root } from "hast";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";
import type { ViewBlock } from "@/lib/chat-render";
import { findMatchOffsets } from "@/lib/transcript-highlight";

/** react-markdown と同じ設定。生の HTML は文字列のまま描かれる。 */
const markdownProcessor = unified()
	.use(remarkParse)
	.use(remarkGfm)
	.use(remarkRehype, { allowDangerousHtml: true });

/** 空白だけのテキストを React が描かない要素 (hast-util-to-jsx-runtime と同じ)。 */
const TABLE_ELEMENTS = new Set(["table", "tbody", "thead", "tfoot", "tr"]);

const WHITESPACE_ONLY = /^[ \t\n\f\r]*$/;

/** これを超えたらキャッシュを捨てる。チャットを渡り歩いても際限なく溜めないため。 */
const PLAIN_TEXT_CACHE_LIMIT = 5000;

const plainTextCache = new Map<string, string>();

function collectText(node: Root | Element, into: string[]): void {
	const inTable = node.type === "element" && TABLE_ELEMENTS.has(node.tagName);
	for (const child of node.children) {
		if (child.type === "element") collectText(child, into);
		else if (child.type === "raw") into.push(child.value);
		else if (child.type === "text" && !(inTable && WHITESPACE_ONLY.test(child.value))) {
			into.push(child.value);
		}
	}
}

/** Markdown を描いたときに画面に出る文字列。 */
export function markdownPlainText(markdown: string): string {
	const tree = markdownProcessor.runSync(markdownProcessor.parse(markdown));
	const parts: string[] = [];
	collectText(tree, parts);
	return parts.join("");
}

/**
 * 変換は重いが、元テキストからは描画後の一致を判断できない (`pod`man が "podman" になるなど) ため、
 * 全メッセージを変換して結果を使い回す。
 */
function cachedMarkdownPlainText(markdown: string): string {
	const cached = plainTextCache.get(markdown);
	if (cached !== undefined) return cached;
	if (plainTextCache.size >= PLAIN_TEXT_CACHE_LIMIT) plainTextCache.clear();
	const text = markdownPlainText(markdown);
	plainTextCache.set(markdown, text);
	return text;
}

/** 本文欄 (data-message-body) に描かれる文字列。本文欄を持たないブロックは null。 */
function bodyText(block: ViewBlock): string | null {
	if (block.kind !== "text") return null;
	if (block.role !== "user" && block.role !== "assistant") return null;
	const fileNames = block.controls.map((control) => control.file_name).join("");
	if (block.role === "user") return block.text + fileNames;
	return cachedMarkdownPlainText(block.text) + fileNames;
}

/** 一致のあるブロックの番号と、そのブロック内の一致数。番号の昇順。 */
export interface BlockMatchCount {
	index: number;
	count: number;
}

/**
 * 履歴の各ブロックの一致数を数える。
 * 生成中の応答は最後のブロックの次 (blocks.length) として数える。これは文字が届くたびに変わるため、キャッシュしない。
 */
export function countTranscriptMatches(
	blocks: readonly ViewBlock[],
	streamingText: string,
	query: string,
): BlockMatchCount[] {
	if (query.length === 0) return [];
	const needle = query.toLowerCase();
	const counts: BlockMatchCount[] = [];
	const add = (index: number, text: string | null) => {
		const count = text === null ? 0 : findMatchOffsets(text, needle).length;
		if (count > 0) counts.push({ index, count });
	};
	for (const [index, block] of blocks.entries()) add(index, bodyText(block));
	if (streamingText.length > 0) {
		add(blocks.length, markdownPlainText(streamingText));
	}
	return counts;
}

/** 全体で matchIndex 番目 (0 起点) の一致が、どのブロックの何番目か。範囲外は最後の一致に寄せる。 */
export function locateTranscriptMatch(
	counts: readonly BlockMatchCount[],
	matchIndex: number,
): { index: number; occurrence: number } | null {
	let remaining = matchIndex;
	for (const { index, count } of counts) {
		if (remaining < count) return { index, occurrence: remaining };
		remaining -= count;
	}
	const last = counts.at(-1);
	return last === undefined ? null : { index: last.index, occurrence: last.count - 1 };
}

/** blockIndex より前のブロックにある一致の数。 */
export function countMatchesBefore(counts: readonly BlockMatchCount[], blockIndex: number): number {
	let total = 0;
	for (const { index, count } of counts) {
		if (index >= blockIndex) break;
		total += count;
	}
	return total;
}
